import React, { useMemo } from "react";
import { compose } from "redux";
import {
  withStyles,
  withBreakpoint,
  withMobile,
  Text,
} from "components/common";
import { EMPTY_STRING, PAYMENT_STATUS } from "utils/constants";
import Table from "components/common/Table";
import { convertToShortDate } from "utils/convert-date";
import sortDate from "utils/sortDate";
import formatCurrency from "utils/formatCurrency";

const [red] = ["#FF8B89"];

const isSettledButReturned = (data) => {
  return new Date(data.returned_at) > new Date(data.settled_at);
};

const isReturned = (data) => {
  return (
    (data.status || "") === PAYMENT_STATUS.RETURNED ||
    isSettledButReturned(data)
  );
};

const computeColumns = (belowMd) =>
  belowMd
    ? [
        {
          Header: "Initiated By",
          accessor: (data) => (
            <div className="flex flex-col">
              <Text className="">
                {data.is_paid_by_user ? "You" : data.paidBy || ""}
              </Text>
              <Text
                className={`${
                  isReturned(data) ? "" : "text-gray-600"
                } text-xs -mt-1 md:mt-0`}
              >
                {convertToShortDate(data.created_at || "")}
              </Text>
            </div>
          ),
        },
        {
          Header: "Amount",
          style: { paddingTop: 0, paddingBottom: 0 }, // avoid expanding the row when this cell is taller
          accessor: (data) => (
            <div className="flex flex-col">
              <Text>
                {`${data.amount ? formatCurrency(data.amount.toFixed(2)) : ""}`}
              </Text>
              {(data.status || "") === PAYMENT_STATUS.CANCELED ? (
                <Text style={{ color: red }} className="text-xs -mt-1 md:mt-0">
                  canceled
                </Text>
              ) : (data.status || "") === PAYMENT_STATUS.PENDING ? (
                <Text className="text-gray-600 text-xs -mt-1 md:mt-0">
                  pending
                </Text>
              ) : (data.status || "") === PAYMENT_STATUS.RETURNED ||
                isSettledButReturned(data) ? (
                <Text className="text-xs -mt-1 md:mt-0">returned</Text>
              ) : (
                <Text className="text-gray-600 text-xs capitalize -mt-1 md:mt-0">
                  {(data.status || "") !== PAYMENT_STATUS.SETTLED
                    ? data.status
                    : null}
                </Text>
              )}
            </div>
          ),
        },
        {
          Header: "Collected",
          accessor: (data) =>
            [PAYMENT_STATUS.SETTLED, PAYMENT_STATUS.RETURNED].includes(
              data.status
            )
              ? new Date(data.returned_at || data.settled_at || data.updated_at)
              : undefined,
          Cell: (data) => {
            const value = data.row.values["Collected"];
            let label = "";
            if (value) {
              if (isReturned(data.row.original)) {
                label = (
                  <>
                    Failed/Returned
                    <br />
                    {convertToShortDate(value)}
                  </>
                );
              } else {
                label = convertToShortDate(value);
              }
            }
            return label;
          },
        },
      ]
    : [
        {
          Header: "Initiated",
          style: { width: "200px" },
          accessor: (data) => new Date(data.created_at),
          Cell: ({ value }) => convertToShortDate(value),
        },
        {
          Header: "Initiated By",
          accessor: "paidBy",
        },
        {
          Header: "Amount",
          style: { paddingTop: 0, paddingBottom: 0, width: "200px" }, // avoid expanding the row when this cell is taller
          accessor: (data) =>
            `${data.amount ? formatCurrency(data.amount.toFixed(2)) : ""}`,
          Cell: (data) => (
            <div>
              <div>{data.row.values.Amount}</div>
              {(data.row.original.status || "") === PAYMENT_STATUS.PENDING ? (
                <div className="font-normal text-gray-600 text-xxs">
                  pending
                </div>
              ) : (data.row.original.status || "") ===
                PAYMENT_STATUS.CANCELED ? (
                <div style={{ color: red }} className="font-normal text-xxs">
                  canceled
                </div>
              ) : isReturned(data.row.original) ? (
                <div className="font-normal text-xxs">returned</div>
              ) : (
                ""
              )}
            </div>
          ),
        },
        {
          Header: "Date Collected",
          style: { width: "270px" },
          accessor: (data) =>
            [PAYMENT_STATUS.SETTLED, PAYMENT_STATUS.RETURNED].includes(
              data.status
            )
              ? new Date(data.returned_at || data.settled_at || data.updated_at)
              : undefined,
          Cell: (data) => {
            const value = data.row.values["Date Collected"];
            let label = "";
            if (value) {
              label = (
                <div className="flex justify-between">
                  <span>{`${
                    isReturned(data.row.original) ? "Failed/Returned " : ""
                  }`}</span>
                  <span>{convertToShortDate(value)}</span>
                </div>
              );
            }
            return label;
          },
        },
      ];

const PaymentsTable = ({
  payments = [],
  user,
  breakpointContext: { isBelow },
}) => {
  const columns = useMemo(() => computeColumns(isBelow("md")), [isBelow]);
  const filteredHistory = useMemo(
    () =>
      payments.map((payment) => {
        let paidBy;
        if (payment.is_paid_by_user)
          paidBy = `${user.first_name} ${user.last_name}`;
        else if (
          payment.category === "credit" ||
          payment.category === "redemption"
        )
          paidBy = "TomoCredit";
        else paidBy = "Auto Pay";
        return { ...payment, paidBy };
      }),
    [payments, user.first_name, user.last_name]
  );

  const getRowStyle = (row) => {
    return {
      backgroundColor: isReturned(row.original) ? red : EMPTY_STRING,
    };
  };

  return (
    <div>
      <div className="my-6">
        {filteredHistory.length ? (
          <Table
            id="transactions-table"
            data={sortDate(filteredHistory)}
            columns={columns}
            getRowStyle={getRowStyle}
          />
        ) : (
          <div>You don't have any payments yet!</div>
        )}
      </div>
    </div>
  );
};

export default compose(withMobile, withStyles, withBreakpoint)(PaymentsTable);
